/**
 * Per-route SEO metadata.
 *
 * Every route gets its own title, description and canonical URL. Previously a
 * single <SEOComponent> was mounted once in Main.js, so all 20 routes shared
 * one title and one canonical tag, which reads as duplicate content.
 *
 * Titles are kept under ~60 characters and descriptions under ~155 so search
 * engines do not truncate them in the results page.
 */

export const SITE_URL = "https://happytechnovation.com";
export const SITE_NAME = "Happy Technovation";
export const DEFAULT_SHARE_IMAGE = `${SITE_URL}/HT_LOGO_1.png`;

export const ORGANISATION = {
  legalName: "Happy Technovation",
  email: "info@happytechnovation.com",
  telephone: "+91-6360501798",
  streetAddress: "16th Main Road, Vijayanagara",
  addressLocality: "Bangalore",
  addressRegion: "Karnataka",
  // TODO(happytechnovation): add the real postal code. Left blank deliberately
  // — a guessed postcode in structured data weakens local-search matching
  // rather than helping it.
  postalCode: "",
  addressCountry: "IN",
  // Sourced from the About page copy ("Founded in 2019").
  foundingDate: "2019",
  sameAs: [
    "https://www.linkedin.com/in/happy-technovation/",
    "https://www.facebook.com/profile.php?id=100081588700211",
  ],
};

/**
 * `service: true` adds a schema.org Service block describing that offering, so
 * the service pages can qualify for richer results than a plain web page.
 */
const PAGES = {
  "/": {
    title: "CAD Automation, AI/ML & PLM Engineering Services",
    description:
      "Happy Technovation delivers CAD automation, automotive design, AI/ML, computer vision, full stack development, PLM solutions and IT staffing for engineering teams.",
  },
  "/about": {
    title: "About Us",
    description:
      "Meet Happy Technovation — a Bangalore-based engineering services company helping automotive, aerospace, railway and energy teams design faster and smarter.",
  },
  "/staffing": {
    title: "Engineering & IT Staffing Solutions",
    description:
      "Contract, contract-to-hire and permanent engineering and IT staffing. We place vetted CAD, PLM, AI/ML and full stack specialists onto your projects quickly.",
    service: "Engineering and IT Staffing",
  },
  "/cadautomation": {
    // Naming the CAD systems is deliberate. "CAD automation" alone is a broad,
    // heavily contested head term; "CATIA automation services" and
    // "NX customization" are the long-tail queries a company this size can
    // realistically rank for, and they carry far higher buying intent.
    title: "CAD Automation Services | CATIA, NX & CREO Customization",
    description:
      "CAD automation and customization for CATIA, NX, CREO and Solid Edge. Automate parametric design, drawing generation and data migration. Bangalore, India.",
    service: "CAD Automation and Customization",
    faqs: [
      {
        q: "Which CAD platforms do you automate?",
        a: "We build automation and customization for CATIA, Siemens NX, PTC CREO, Autodesk and Solid Edge, including parametric design automation, automated drawing generation and CAD data migration.",
      },
      {
        q: "How much design time does CAD automation actually save?",
        a: "It depends on how repetitive the task is. Automating parametric design updates, drawing generation and standards checking typically removes the manual, repeatable portion of the work and reduces the rework caused by human error.",
      },
      {
        q: "Can you integrate CAD automation with our PLM or ERP system?",
        a: "Yes. We integrate CAD automation with PLM and ERP platforms including Windchill, Teamcenter, 3DEXPERIENCE and Enovia so that design data flows through to downstream systems without manual re-entry.",
      },
    ],
  },
  "/automotivedesign": {
    title: "Automotive Design & Engineering Services",
    description:
      "Interior and exterior automotive design support — door trims, instrument panels, sealing systems and window regulators — delivered to OEM standards.",
    service: "Automotive Design Engineering",
  },
  "/aiml": {
    // Targets "AI for CAD" and "AI drawing check" rather than bare "AI/ML",
    // which is unwinnable. Engineering-specific AI is a narrow field with far
    // less competition and a much better-qualified visitor.
    title: "AI for CAD & Engineering | AI Drawing Check, CAD Intelligence",
    description:
      "AI built into your CAD workflow — AI drawing checks, 3D shape similarity search, feature detection and DFMEA-linked validation for CATIA, CREO, NX and AutoCAD.",
    service: "Artificial Intelligence and Machine Learning",
    faqs: [
      {
        q: "Which CAD systems does your AI work with?",
        a: "Our AI assistant integrates with CATIA, CREO, NX and AutoCAD. It reads the live model tree — sketches, features, bodies and assemblies — and responds to plain-language questions inside the CAD session.",
      },
      {
        q: "Does our CAD data leave our environment?",
        a: "No. Solutions are developed and deployed inside your working environment and integrated with your existing CAD and PLM ecosystem, so your product data stays where it is.",
      },
      {
        q: "What can AI check on an engineering drawing?",
        a: "Dimensions, annotations, GD&T and compliance against your own design rules and standards. It can also detect and interpret marks and symbols directly from PDF drawing sets for regulation compliance review.",
      },
      {
        q: "Do we pay per user for these AI tools?",
        a: "No. There is no per-user licensing fee. Automation and AI solutions are built for your environment on a one-time development cost, with ongoing support for issue resolution and enhancements.",
      },
    ],
  },
  "/computervision": {
    title: "Computer Vision Solutions",
    description:
      "Computer vision systems for inspection, defect detection, object tracking and image processing — turning camera feeds into reliable production decisions.",
    service: "Computer Vision",
  },
  "/fullstack": {
    title: "Full Stack Development Services",
    description:
      "End-to-end web and mobile development. React, Node.js, Python and cloud infrastructure delivered by one team, from interface design through to deployment.",
    service: "Full Stack Development",
  },
  "/digitalmarketing": {
    title: "Digital Marketing Services",
    description:
      "SEO, performance marketing, content and social campaigns that grow qualified traffic and put engineering and technology brands in front of the right buyers.",
    service: "Digital Marketing",
  },
  "/plmsolution": {
    title: "PLM Solutions | Windchill, Teamcenter & 3DEXPERIENCE",
    description:
      "PLM consulting and implementation for Windchill, Teamcenter, 3DEXPERIENCE and Enovia. Migration, integration and workflow automation for product data.",
    service: "Product Lifecycle Management Solutions",
    faqs: [
      {
        q: "Which PLM platforms do you implement?",
        a: "We work with PTC Windchill, Siemens Teamcenter, Dassault 3DEXPERIENCE and Enovia, covering implementation, customization, migration and integration with CAD and ERP systems.",
      },
      {
        q: "Can you migrate our existing product data into a new PLM system?",
        a: "Yes. Data migration is a core part of our PLM work, including mapping legacy product data, preserving revision history and validating the migrated structure against the source system.",
      },
    ],
  },
  "/success": {
    title: "Case Studies | CAD & PLM Automation Engagements",
    description:
      "How we work and what we have built — CAD automation, regulation checking, mould tooling and routing engagements, described by sector rather than by customer name.",
    faqs: [
      {
        q: "Why are your clients not named?",
        a: "We do not publish customer identities or their product data. Engagements are described by sector so the engineering problem and the approach are clear without disclosing who we worked with. We treat your programme the same way.",
      },
      {
        q: "How does an engagement usually start?",
        a: "Almost always with one task that an engineer repeats on every programme — a check, a geometry build, a report. We look at what it costs today, what rules it follows, and whether those rules are consistent enough to automate reliably.",
      },
      {
        q: "Who owns the automation you build?",
        a: "It is developed inside your working environment and integrated into your existing CAD and PLM ecosystem, on a one-time development cost with no per-user licensing. Support for issue resolution and enhancements continues after handover.",
      },
    ],
  },
  "/careers": {
    title: "Careers",
    description:
      "Join Happy Technovation. Explore open engineering, AI/ML, CAD and full stack roles in Bangalore and apply online in a couple of minutes.",
  },
  "/contact": {
    title: "Contact Us",
    description:
      "Talk to Happy Technovation about your CAD automation, AI/ML, PLM or staffing requirement. Call +91 63605 01798 or email info@happytechnovation.com.",
  },
};

/**
 * Component-preview routes render a bare section with no header or footer.
 * They are excluded from the sitemap and marked noindex so they cannot compete
 * with the real pages in search results.
 */
const NOINDEX_PATHS = [
  "/header",
  "/components",
  "/footermain",
  "/carouselmain",
  "/partners",
  "/model",
  "/processprogresscircle",
];

const FALLBACK = {
  title: "Page Not Found",
  description:
    "The page you are looking for is not available. Browse our CAD automation, AI/ML, computer vision, full stack and PLM services instead.",
  noindex: true,
};

export function getSeoForPath(pathname = "/") {
  // Trailing slashes and casing vary between links and crawlers; normalise both
  // so "/About/" and "/about" resolve to the same entry.
  const key = pathname.toLowerCase().replace(/\/+$/, "") || "/";

  if (NOINDEX_PATHS.includes(key)) {
    return { ...FALLBACK, title: "Component Preview", path: key };
  }

  const page = PAGES[key];
  if (!page) return { ...FALLBACK, path: key };

  return { ...page, path: key };
}

export default PAGES;
